import styled from 'styled-components';

export const Main = styled.main`
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
  padding: 0;
  display: flex;
  flex-direction: column;
`;

export const ContentContainer = styled.div`
  flex: 1;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.cardBackground};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => `0 0 40px 0 ${theme.colors.shadow}`};
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin: 20px;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    min-height: auto;
    height: 100%;
    margin: 30px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin: 20px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    border-radius: ${({ theme }) => theme.borderRadius.large};
    margin: 16px;
  }
`;

export const HeaderContainer = styled.div`
  position: relative;
  padding: 20px 80px;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    padding: 30px 40px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 24px 24px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 20px 20px;
  }
`;

export const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-bottom: 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

export const Logo = styled.img`
  height: 48px;
  width: auto;
  object-fit: contain;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 40px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 36px;
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
    flex-direction: column;
  }
`;

export const ActionButton = styled.button`
  background-color: ${({ $variant, theme }) =>
    $variant === 'primary' ? theme.colors.primary : 'transparent'};
  color: ${({ $variant, theme }) =>
    $variant === 'primary' ? theme.colors.primaryForeground : theme.colors.text};
  padding: 15px 30px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.normal};
  font-weight: 600;
  transition: all 0.2s ease;
  font-family: ${({ theme }) => theme.fonts.primary};

  &:hover {
    opacity: 0.9;
    ${({ $variant, theme }) =>
      $variant !== 'primary' && `background-color: ${theme.colors.border};`}
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
    font-size: ${({ theme }) => theme.fontSizes.small};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
    padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
    font-size: ${({ theme }) => theme.fontSizes.small};

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

export const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

export const LogoCircle = styled.div`
  width: 85px;
  height: 84px;
  background-color: ${({ theme }) => theme.colors.cardBackground};
  border-radius: 41px;
  box-shadow: ${({ theme }) => `0 0 40px 0 ${theme.colors.blue}`};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  img {
    width: 82px;
    height: 82px;
    object-fit: cover;
    border-radius: 50%;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 70px;
    height: 70px;

    img {
      width: 68px;
      height: 68px;
    }
  }
`;

export const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes.large};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  letter-spacing: -0.25px;
  margin: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes.medium};
  }
`;

export const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.normal};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  line-height: 1.5;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes.small};
  }
`;

export const InfoBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  background-color: ${({ theme }) => theme.colors.inputBackground};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.colors.text};

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const MessagesScrollContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px 80px;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    padding: 30px 40px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 24px 24px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 20px 20px;
  }

  /* Custom scrollbar */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => `${theme.colors.border} transparent`};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.border};
    border-radius: 3px;
  }
`;

export const MessageContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  max-width: 900px;
  margin-left: ${({ $isUser }) => ($isUser ? 'auto' : '0')};
  margin-right: ${({ $isUser }) => ($isUser ? '0' : 'auto')};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    gap: 10px;
    margin-bottom: 20px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 8px;
    margin-bottom: 16px;
  }
`;

export const MessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  gap: 6px;
  flex: 1;
  ${({ $isUser }) => $isUser && 'max-width: 100%;'}

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    gap: 4px;
    ${({ $isUser }) => $isUser && 'max-width: 75%;'}
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    ${({ $isUser }) => $isUser && 'max-width: 85%;'}
  }
`;

export const MessageBubble = styled.div`
  padding: 14px 18px;
  border-radius: 12px;
  background-color: ${({ $isUser, theme }) =>
    $isUser ? theme.colors.primary : theme.colors.inputBackground};
  color: ${({ $isUser, theme }) => ($isUser ? theme.colors.primaryForeground : theme.colors.text)};
  font-size: ${({ theme }) => theme.fontSizes.normal};
  line-height: 1.6;
  box-shadow: ${({ theme }) => `0 0 10px ${theme.colors.shadow}`};
  word-wrap: break-word;
  max-width: 100%;
  font-family: ${({ theme }) => theme.fonts.primary};

  /* Formatting for bot messages */
  p {
    margin: 0 0 8px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    margin: 8px 0;
    padding-left: 20px;
  }

  li {
    margin: 4px 0;
  }

  strong {
    font-weight: 600;
  }

  code {
    background-color: ${({ $isUser, theme }) =>
      $isUser ? 'rgba(255, 255, 255, 0.2)' : theme.colors.border};
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Monaco', 'Courier New', monospace;
    font-size: 13px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 12px 16px;
    font-size: ${({ theme }) => theme.fontSizes.normal};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 10px 14px;
    font-size: ${({ theme }) => theme.fontSizes.small};
  }
`;

export const TimeStamp = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 0 4px;
  align-self: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 11px;
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};

  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  h3 {
    font-size: ${({ theme }) => theme.fontSizes.medium};
    color: ${({ theme }) => theme.colors.text};
    margin: 0 0 8px 0;
  }

  p {
    font-size: ${({ theme }) => theme.fontSizes.normal};
    margin: 0;
  }
`;
